<app-ui-page-header title="Segurança" subtitle="Funções e permissões, contas de serviço e chaves de API." />

<div class="mt-6 grid gap-6">
  <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
    <app-ui-section-header
      [label]="activeTab() === 'roles' ? 'Permissões' : 'Contas de serviço'"
      [help]="
        activeTab() === 'roles'
          ? 'Funções agrupam permissões usadas por pessoas na organização.'
          : 'Credenciais técnicas isolam integrações, automações e chaves de API.'
      "
    >
      <div sectionActions class="flex flex-wrap items-center gap-2">
        <button appButton type="button" variant="ghost" (click)="load()" [disabled]="loading()">
          <ng-icon name="heroArrowPath" size="16" aria-hidden="true" />
          Atualizar
        </button>
        @if (activeTab() === 'roles') {
          <button
            appButton
            type="button"
            variant="primary"
            (click)="openCreateRole()"
            [disabled]="!canCreateRoles()"
            [attr.title]="!canCreateRoles() ? createRolePermissionHint : null"
          >
            <ng-icon name="heroPlus" size="16" aria-hidden="true" />
            Criar função
          </button>
        } @else {
          <button
            appButton
            type="button"
            variant="primary"
            (click)="openCreateServiceAccount()"
            [disabled]="!canCreateServiceAccounts() || !canAssignRoles()"
            [attr.title]="
              !canCreateServiceAccounts()
                ? createServiceAccountPermissionHint
                : !canAssignRoles()
                  ? assignRolePermissionHint
                  : null
            "
          >
            <ng-icon name="heroPlus" size="16" aria-hidden="true" />
            Nova conta
          </button>
        }
      </div>
    </app-ui-section-header>
  </section>

  @switch (activeTab()) {
    @case ('roles') {
      <section class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
        <app-ui-section-header
          [label]="'Funções (' + visibleRoles().length + ')'"
          help="Funções ativas, quantidade de permissões e usuários associados."
        />
        <div appUiDataTableShell>
          <table appUiDataTable>
            <thead appUiDataTableHead>
              <tr>
                <th appUiDataTableHeaderCell class="w-[36%]">Função</th>
                <th appUiDataTableHeaderCell class="w-[18%]">Tipo</th>
                <th appUiDataTableHeaderCell class="w-[16%]">Permissões</th>
                <th appUiDataTableHeaderCell class="w-[14%]">Usuários</th>
                <th appUiDataTableHeaderCell class="w-[16%] text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              @for (role of visibleRoles(); track role.id) {
                <tr appUiDataTableRow>
                  <td appUiDataTableCell>
                    <p class="truncate font-semibold text-ink">{{ role.name }}</p>
                    @if (role.description) {
                      <p appUiHint class="mt-1 truncate">{{ role.description }}</p>
                    }
                  </td>
                  <td appUiDataTableCell>
                    <span appBadge tone="neutral" size="xs">{{
                      role.isSystem ? 'Sistema' : 'Organização'
                    }}</span>
                  </td>
                  <td appUiDataTableCell class="font-semibold text-ink">{{ permissionCount(role) }}</td>
                  <td appUiDataTableCell class="font-semibold text-ink">{{ userCountForRole(role.id) }}</td>
                  <td appUiDataTableCell class="relative">
                    <div appUiDataTableActions>
                      @if (!role.isSystem) {
                        <button
                          appButton
                          type="button"
                          variant="ghost"
                          size="sm"
                          [ngpMenuTrigger]="roleActionsMenu"
                          ngpMenuTriggerPlacement="bottom-end"
                          [disabled]="!canUpdateRoles() && !canArchiveRoles()"
                          aria-label="Ações da função"
                        >
                          <ng-icon name="heroEllipsisVertical" size="16" aria-hidden="true" />
                        </button>
                        <ng-template #roleActionsMenu>
                          <div appMenuPanel class="min-w-36">
                            <button
                              type="button"
                              appMenuItem
                              (click)="openEditRole(role)"
                              [disabled]="!canUpdateRoles()"
                            >
                              Editar
                            </button>
                            <button
                              type="button"
                              appMenuItem
                              tone="danger"
                              (click)="openDeleteRole(role)"
                              [disabled]="!canArchiveRoles()"
                            >
                              Excluir
                            </button>
                          </div>
                        </ng-template>
                      }
                    </div>
                  </td>
                </tr>
              } @empty {
                <tr>
                  <td appUiDataTableEmptyCell colspan="5">
                    <app-ui-empty-state
                      compact
                      title="Nenhuma função encontrada"
                      description="Funções criadas para permissões da organização aparecerão aqui."
                    />
                  </td>
                </tr>
              }
            </tbody>
          </table>
        </div>
      </section>

      @if (creatingRole()) {
        <div appDialogOverlay>
          <section
            appDialog
            class="flex h-[min(48rem,calc(100dvh-2rem))] !w-[min(62rem,calc(100vw-2rem))] !max-w-none flex-col overflow-hidden"
          >
            <div class="shrink-0 border-b border-line pb-4">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 appDialogTitle class="text-base">Criar função</h3>
                  <p appDialogDescription>
                    Nomeie a função e escolha o conjunto de permissões que ela concede.
                  </p>
                </div>
                <button
                  appButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  (click)="closeCreateRole()"
                  aria-label="Fechar"
                >
                  <ng-icon name="heroXMark" size="16" aria-hidden="true" />
                </button>
              </div>
            </div>

            <form
              class="flex min-h-0 flex-1 flex-col"
              [formGroup]="roleForm"
              (ngSubmit)="createRole()"
            >
              <div class="grid h-full min-h-0 grid-rows-[minmax(0,1fr)_auto]">
                <div class="min-h-0 overflow-y-auto overflow-x-hidden py-5 pr-1">
                  <div class="grid gap-4">
                    <div class="grid gap-3 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                      <label class="grid gap-1.5">
                        <span appUiLabel>Nome</span>
                        <input
                          appInput
                          formControlName="name"
                          autocomplete="off"
                          placeholder="Operador de suporte"
                          [readonly]="!canCreateRoles()"
                        />
                      </label>
                      <label class="grid gap-1.5">
                        <span appUiLabel>Descrição</span>
                        <textarea
                          appTextarea
                          formControlName="description"
                          placeholder="Acesso necessário para atuar na rotina."
                          [readonly]="!canCreateRoles()"
                        ></textarea>
                      </label>
                    </div>

                    <div appUiFilterBar>
                      <label appUiFilterField class="min-w-64 flex-1">
                        <span appUiFilterLabel>Buscar permissão</span>
                        <span appUiFilterSearch class="!max-w-none">
                          <ng-icon
                            name="heroMagnifyingGlass"
                            size="15"
                            aria-hidden="true"
                            class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                          />
                          <input
                            appInput
                            leadingIcon
                            type="search"
                            [value]="permissionSearchTerm()"
                            (input)="setPermissionSearchTerm($any($event.target).value)"
                            placeholder="Descrição, domínio ou chave"
                          />
                        </span>
                      </label>
                      <div appUiFilterField class="w-full sm:w-56">
                        <span
                          id="create-role-domain-label"
                          appUiFilterLabel
                        >
                          Domínio
                        </span>
                        <div
                          appSelect
                          aria-labelledby="create-role-domain-label"
                          [ngpSelectValue]="permissionDomainFilter()"
                          (ngpSelectValueChange)="selectPermissionDomain($event)"
                        >
                          <span>{{ permissionDomainLabel(permissionDomainFilter()) }}</span>
                          <div *ngpSelectPortal appSelectDropdown>
                            <div appSelectOption ngpSelectOptionValue="all">Todos os domínios</div>
                            @for (domain of permissionDomains(); track domain) {
                              <div appSelectOption [ngpSelectOptionValue]="domain">
                                {{ domain }}
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                      <div appUiFilterActions class="text-sm font-semibold text-ink">
                        {{ newRoleSelectedCount() }} selecionadas
                      </div>
                    </div>

                    <div class="grid gap-x-5 gap-y-6 xl:grid-cols-3">
                      @for (domain of visiblePermissionDomains(); track domain) {
                        <section
                          class="grid content-start gap-3 border-t border-line pt-3 first:border-t-0 first:pt-0 xl:first:border-t xl:first:pt-3"
                        >
                          <app-ui-section-header
                            [label]="domain"
                            [help]="'Permissões disponíveis para ' + domain + '.'"
                          >
                            <div sectionActions class="flex flex-wrap gap-1">
                              <button
                                appButton
                                type="button"
                                variant="ghost"
                                size="sm"
                                (click)="selectAllNewRoleDomain(domain)"
                              >
                                Todas
                              </button>
                              <button
                                appButton
                                type="button"
                                variant="ghost"
                                size="sm"
                                (click)="clearNewRoleDomain(domain)"
                              >
                                Limpar
                              </button>
                            </div>
                          </app-ui-section-header>
                          <div class="grid gap-2">
                            @for (
                              permission of permissionsForDomain(domain);
                              track permission.key
                            ) {
                              <div
                                appUiRecordRow
                                [interactive]="false"
                                class="min-h-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-2 px-3 py-2"
                              >
                                <button
                                  appCheckbox
                                  type="button"
                                  class="mt-0.5"
                                  [ngpCheckboxChecked]="newRolePermissionChecked(permission.key)"
                                  [ngpCheckboxDisabled]="!canCreateRoles()"
                                  (ngpCheckboxCheckedChange)="toggleNewRolePermission(permission.key, $event)"
                                  [attr.aria-label]="'Alternar permissão ' + permission.key"
                                >
                                  <ng-icon name="heroCheck" size="13" aria-hidden="true" class="opacity-0 transition-opacity group-data-[checked]:opacity-100" />
                                </button>
                                <span class="min-w-0">
                                  <span
                                    class="flex flex-wrap items-center gap-2 font-semibold leading-5 text-ink"
                                  >
                                    <span>{{ permission.description }}</span>
                                    @if (permission.isSensitive) {
                                      <span appBadge tone="danger" size="xs">
                                        Sensível
                                      </span>
                                    }
                                  </span>
                                  <span appUiHint class="mt-1 block break-all">{{
                                    permission.key
                                  }}</span>
                                </span>
                              </div>
                            }
                          </div>
                        </section>
                      }
                    </div>
                  </div>
                </div>

                <div class="flex shrink-0 justify-end gap-2 border-t border-line pt-4">
                  <button appButton type="button" variant="secondary" (click)="closeCreateRole()">
                    Cancelar
                  </button>
                  <button
                    appButton
                    type="submit"
                    variant="primary"
                    [disabled]="saving() || roleForm.invalid || !canCreateRoles()"
                  >
                    <ng-icon name="heroPlus" size="16" aria-hidden="true" />
                    Criar função
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      }

      @if (editingRole(); as role) {
        <div appDialogOverlay>
          <section
            appDialog
            class="flex h-[min(48rem,calc(100dvh-2rem))] !w-[min(62rem,calc(100vw-2rem))] !max-w-none flex-col overflow-hidden"
          >
            <div class="shrink-0 border-b border-line pb-4">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <h3 appDialogTitle class="text-base">Editar {{ role.name }}</h3>
                  <p appDialogDescription>
                    Ajuste o nome visível e o conjunto de permissões desta função.
                  </p>
                </div>
                <button
                  appButton
                  type="button"
                  variant="ghost"
                  size="sm"
                  (click)="closeEditRole()"
                  aria-label="Fechar"
                >
                  <ng-icon name="heroXMark" size="16" aria-hidden="true" />
                </button>
              </div>
            </div>

            <form
              class="flex min-h-0 flex-1 flex-col"
              [formGroup]="editRoleForm"
              (ngSubmit)="saveEditedRole()"
            >
              <div class="grid h-full min-h-0 grid-rows-[minmax(0,1fr)_auto]">
                <div class="min-h-0 overflow-y-auto overflow-x-hidden py-5 pr-1">
                  <div class="grid gap-4">
                    <div class="grid gap-3 md:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)]">
                      <label class="grid gap-1.5">
                        <span appUiLabel>Nome</span>
                        <input
                          appInput
                          formControlName="name"
                          autocomplete="off"
                          [readonly]="!canUpdateRoles()"
                        />
                      </label>
                      <label class="grid gap-1.5">
                        <span appUiLabel>Descrição</span>
                        <textarea
                          appTextarea
                          formControlName="description"
                          placeholder="Acesso necessário para atuar na rotina."
                          [readonly]="!canUpdateRoles()"
                        ></textarea>
                      </label>
                    </div>

                    <div appUiFilterBar>
                      <label appUiFilterField class="min-w-64 flex-1">
                        <span appUiFilterLabel>Buscar permissão</span>
                        <span appUiFilterSearch class="!max-w-none">
                          <ng-icon
                            name="heroMagnifyingGlass"
                            size="15"
                            aria-hidden="true"
                            class="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-muted"
                          />
                          <input
                            appInput
                            leadingIcon
                            type="search"
                            [value]="permissionSearchTerm()"
                            (input)="setPermissionSearchTerm($any($event.target).value)"
                            placeholder="Descrição, domínio ou chave"
                          />
                        </span>
                      </label>
                      <div appUiFilterField class="w-full sm:w-56">
                        <span id="edit-role-domain-label" appUiFilterLabel>
                          Domínio
                        </span>
                        <div
                          appSelect
                          aria-labelledby="edit-role-domain-label"
                          [ngpSelectValue]="permissionDomainFilter()"
                          (ngpSelectValueChange)="selectPermissionDomain($event)"
                        >
                          <span>{{ permissionDomainLabel(permissionDomainFilter()) }}</span>
                          <div *ngpSelectPortal appSelectDropdown>
                            <div appSelectOption ngpSelectOptionValue="all">Todos os domínios</div>
                            @for (domain of permissionDomains(); track domain) {
                              <div appSelectOption [ngpSelectOptionValue]="domain">
                                {{ domain }}
                              </div>
                            }
                          </div>
                        </div>
                      </div>
                      <div appUiFilterActions class="text-sm font-semibold text-ink">
                        {{ editedRoleSelectedCount() }} selecionadas
                      </div>
                    </div>

                    <div class="grid gap-x-5 gap-y-6 xl:grid-cols-3">
                      @for (domain of visiblePermissionDomains(); track domain) {
                        <section
                          class="grid content-start gap-3 border-t border-line pt-3 first:border-t-0 first:pt-0 xl:first:border-t xl:first:pt-3"
                        >
                          <app-ui-section-header
                            [label]="domain"
                            [help]="'Permissões disponíveis para ' + domain + '.'"
                          >
                            <div sectionActions class="flex flex-wrap gap-1">
                              <button
                                appButton
                                type="button"
                                variant="ghost"
                                size="sm"
                                (click)="selectAllEditedRoleDomain(domain)"
                              >
                                Todas
                              </button>
                              <button
                                appButton
                                type="button"
                                variant="ghost"
                                size="sm"
                                (click)="clearEditedRoleDomain(domain)"
                              >
                                Limpar
                              </button>
                            </div>
                          </app-ui-section-header>
                          <div class="grid gap-2">
                            @for (
                              permission of permissionsForDomain(domain);
                              track permission.key
                            ) {
                              <div
                                appUiRecordRow
                                [interactive]="false"
                                class="min-h-0 grid-cols-[auto_minmax(0,1fr)] items-start gap-2 px-3 py-2"
                              >
                                <button
                                  appCheckbox
                                  type="button"
                                  class="mt-0.5"
                                  [ngpCheckboxChecked]="editedPermissionChecked(permission.key)"
                                  [ngpCheckboxDisabled]="!canUpdateRoles()"
                                  (ngpCheckboxCheckedChange)="toggleEditedPermission(permission.key, $event)"
                                  [attr.aria-label]="'Alternar permissão ' + permission.key"
                                >
                                  <ng-icon name="heroCheck" size="13" aria-hidden="true" class="opacity-0 transition-opacity group-data-[checked]:opacity-100" />
                                </button>
                                <span class="min-w-0">
                                  <span
                                    class="flex flex-wrap items-center gap-2 font-semibold leading-5 text-ink"
                                  >
                                    <span>{{ permission.description }}</span>
                                    @if (permission.isSensitive) {
                                      <span appBadge tone="danger" size="xs">
                                        Sensível
                                      </span>
                                    }
                                  </span>
                                  <span appUiHint class="mt-1 block break-all">{{
                                    permission.key
                                  }}</span>
                                </span>
                              </div>
                            }
                          </div>
                        </section>
                      }
                    </div>
                  </div>
                </div>

                <div class="flex shrink-0 justify-end gap-2 border-t border-line pt-4">
                  <button appButton type="button" variant="secondary" (click)="closeEditRole()">
                    Cancelar
                  </button>
                  <button
                    appButton
                    type="submit"
                    variant="primary"
                    [disabled]="saving() || editRoleForm.invalid || !canUpdateRoles()"
                  >
                    <ng-icon name="heroCheck" size="16" aria-hidden="true" />
                    Salvar
                  </button>
                </div>
              </div>
            </form>
          </section>
        </div>
      }

      @if (deletingRole(); as role) {
        <div appDialogOverlay>
          <section appDialog>
            <div class="flex items-start justify-between gap-3">
              <div>
                <p class="text-xs font-semibold uppercase tracking-wide text-accent">
                  Excluir função
                </p>
                <h3 appDialogTitle class="mt-1 text-base">{{ role.name }}</h3>
                <p appDialogDescription>
                  Funções excluídas saem das listas ativas. As funções de sistema continuam
                  inalteráveis.
                </p>
                <p class="mt-2 text-sm font-semibold text-ink">{{ deletingRoleImpactLabel() }}</p>
              </div>
              <button
                appButton
                type="button"
                variant="ghost"
                size="sm"
                (click)="closeDeleteRole()"
                aria-label="Fechar"
              >
                <ng-icon name="heroXMark" size="16" aria-hidden="true" />
              </button>
            </div>

            @if (deletingRoleBindings().length > 0) {
              <div class="mt-5 grid gap-1.5">
                <span appUiLabel id="delete-role-transfer-label"
                  >Atribuições atuais</span
                >
                <div
                  appSelect
                  aria-labelledby="delete-role-transfer-label"
                  [ngpSelectValue]="deleteRoleTransferId()"
                  [ngpSelectDisabled]="saving() || !canAssignRoles()"
                  (ngpSelectValueChange)="selectDeleteRoleTransfer($event)"
                  [attr.title]="!canAssignRoles() ? assignRolePermissionHint : null"
                >
                  <span>{{ deleteRoleTransferLabel() }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    <div appSelectOption ngpSelectOptionValue="">Remover atribuições</div>
                    @for (targetRole of deleteRoleTransferOptions(); track targetRole.id) {
                      <div appSelectOption [ngpSelectOptionValue]="targetRole.id">
                        Transferir para {{ targetRole.name }}
                      </div>
                    }
                  </div>
                </div>
                <p appUiHint>
                  {{ deletingRoleBindings().length }} atribuições usam esta função.
                </p>
              </div>
            }

            <div class="mt-5 flex justify-end gap-2">
              <button appButton type="button" variant="secondary" (click)="closeDeleteRole()">
                Cancelar
              </button>
              <button
                appButton
                type="button"
                variant="danger"
                (click)="confirmDeleteRole()"
                [disabled]="
                  saving() ||
                  !canArchiveRoles() ||
                  (deletingRoleBindings().length > 0 && !canAssignRoles())
                "
              >
                <ng-icon name="heroTrash" size="16" aria-hidden="true" />
                Excluir função
              </button>
            </div>
          </section>
        </div>
      }
    }

    @case ('serviceAccounts') {
      <section class="grid gap-5">
        <div class="grid gap-4 border-t border-line pt-5 first:border-t-0 first:pt-0">
          <div appUiDataTableShell>
            <table appUiDataTable>
              <thead appUiDataTableHead>
                <tr>
                  <th appUiDataTableHeaderCell class="w-[34%]">Conta</th>
                  <th appUiDataTableHeaderCell class="w-[16%]">Uso</th>
                  <th appUiDataTableHeaderCell class="w-[12%]">Chaves</th>
                  <th appUiDataTableHeaderCell class="w-[12%]">Funções</th>
                  <th appUiDataTableHeaderCell class="w-[14%]">Estado</th>
                  <th appUiDataTableHeaderCell class="w-[12%] text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                @for (account of visibleServiceAccounts(); track account.id) {
                  <tr appUiDataTableRow>
                    <td appUiDataTableCell>
                      <p class="truncate font-semibold text-ink">{{ account.name }}</p>
                      @if (account.description) {
                        <p appUiHint class="mt-1 truncate">{{ account.description }}</p>
                      }
                    </td>
                    <td appUiDataTableCell class="text-muted">
                      <span class="block truncate">{{
                        serviceAccountKindLabel(account.kind)
                      }}</span>
                    </td>
                    <td appUiDataTableCell class="text-muted">
                      {{ serviceAccountKeyCount(account.id) }}
                    </td>
                    <td appUiDataTableCell class="text-muted">
                      {{ serviceAccountRoleCount(account.id) }}
                    </td>
                    <td appUiDataTableCell>
                      <span appBadge [tone]="serviceAccountStatusTone(account.status)" size="xs">{{
                        serviceAccountStatusLabel(account.status)
                      }}</span>
                    </td>
                    <td appUiDataTableCell>
                      <div appUiDataTableActions>
                        @if (account.status !== 'disabled') {
                          <button
                            appButton
                            type="button"
                            variant="danger"
                            size="sm"
                            (click)="disableServiceAccount(account)"
                            [disabled]="
                              isServiceAccountSaving(account.id) || !canDisableServiceAccounts()
                            "
                            [attr.title]="
                              !canDisableServiceAccounts()
                                ? disableServiceAccountPermissionHint
                                : 'Desabilitar conta de serviço'
                            "
                            aria-label="Desabilitar conta de serviço"
                          >
                            <ng-icon name="heroNoSymbol" size="15" aria-hidden="true" />
                          </button>
                        }
                      </div>
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td appUiDataTableEmptyCell colspan="6">
                      <app-ui-empty-state
                        compact
                        title="Nenhuma conta de serviço"
                        description="Contas técnicas para integrações e automações aparecerão aqui."
                      />
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>

        <div class="grid gap-4 border-t border-line pt-5">
          <app-ui-section-header
            [label]="'Chaves de API (' + visibleApiKeys().length + ')'"
            help="Credenciais revogáveis. A chave completa aparece apenas uma vez ao ser criada."
          >
            <button
              sectionActions
              appButton
              type="button"
              variant="primary"
              (click)="openCreateApiKey()"
              [disabled]="serviceAccountsAvailableForKeys().length === 0 || !canCreateApiKeys()"
              [attr.title]="
                !canCreateApiKeys()
                  ? createApiKeyPermissionHint
                  : serviceAccountsAvailableForKeys().length === 0
                    ? 'Crie uma conta de serviço ativa antes de gerar chaves.'
                    : null
              "
            >
              <ng-icon name="heroKey" size="16" aria-hidden="true" />
              Nova chave
            </button>
          </app-ui-section-header>

          @if (createdRawKey()) {
            <div appUiPanelCard class="grid gap-3 bg-accent-soft">
              <div class="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p appUiPanelText class="!mt-0 font-semibold !text-ink">
                    Chave criada. Copie agora, ela não será exibida novamente.
                  </p>
                  <p appUiPanelText class="!text-xs">
                    O aviso só pode ser dispensado depois da cópia.
                  </p>
                </div>
                <div class="flex flex-wrap gap-2">
                  <button appButton type="button" variant="secondary" (click)="copyCreatedRawKey()">
                    <ng-icon name="heroKey" size="15" aria-hidden="true" />
                    {{ createdRawKeyCopied() ? 'Copiada' : 'Copiar' }}
                  </button>
                  <button
                    appButton
                    type="button"
                    variant="primary"
                    (click)="dismissCreatedRawKey()"
                    [disabled]="!createdRawKeyCopied()"
                  >
                    Já copiei
                  </button>
                </div>
              </div>
              <code appUiPanelCard class="mt-2 block max-h-28 overflow-auto break-all text-xs text-ink">{{
                createdRawKey()
              }}</code>
            </div>
          }

          <div appUiDataTableShell>
            <table appUiDataTable>
              <thead appUiDataTableHead>
                <tr>
                  <th appUiDataTableHeaderCell class="w-[24%]">Chave</th>
                  <th appUiDataTableHeaderCell class="w-[22%]">Conta</th>
                  <th appUiDataTableHeaderCell class="w-[20%]">Expiração</th>
                  <th appUiDataTableHeaderCell class="w-[16%]">Último uso</th>
                  <th appUiDataTableHeaderCell class="w-[10%]">Estado</th>
                  <th appUiDataTableHeaderCell class="w-[8%] text-right">Ações</th>
                </tr>
              </thead>
              <tbody>
                @for (apiKey of visibleApiKeys(); track apiKey.id) {
                  <tr appUiDataTableRow>
                    <td appUiDataTableCell>
                      <p class="truncate font-semibold text-ink">{{ apiKey.name }}</p>
                      <p appUiHint class="mt-1 truncate">{{ apiKey.keyPrefix }}</p>
                    </td>
                    <td appUiDataTableCell class="text-muted">
                      <span class="block truncate">{{
                        serviceAccountLabel(apiKey.serviceAccountId)
                      }}</span>
                    </td>
                    <td appUiDataTableCell class="text-muted">
                      @if (apiKey.expiresAt) {
                        <span class="block truncate">{{
                          apiKey.expiresAt | date: 'dd/MM/yyyy HH:mm'
                        }}</span>
                      } @else {
                        Sem expiração
                      }
                    </td>
                    <td appUiDataTableCell class="text-muted">
                      @if (apiKey.lastUsedAt) {
                        <span class="block truncate">{{
                          apiKey.lastUsedAt | date: 'dd/MM/yyyy HH:mm'
                        }}</span>
                      } @else {
                        Nunca usada
                      }
                    </td>
                    <td appUiDataTableCell>
                      <span appBadge [tone]="apiKeyStatusTone(apiKey.status)" size="xs">{{
                        apiKeyStatusLabel(apiKey.status)
                      }}</span>
                    </td>
                    <td appUiDataTableCell>
                      <div appUiDataTableActions>
                        @if (apiKey.status !== 'revoked') {
                          <button
                            appButton
                            type="button"
                            variant="danger"
                            size="sm"
                            (click)="revokeApiKey(apiKey)"
                            [disabled]="isApiKeySaving(apiKey.id) || !canRevokeApiKeys()"
                            [attr.title]="
                              !canRevokeApiKeys()
                                ? revokeApiKeyPermissionHint
                                : 'Revogar chave de API'
                            "
                            aria-label="Revogar chave de API"
                          >
                            <ng-icon name="heroTrash" size="15" aria-hidden="true" />
                          </button>
                        }
                      </div>
                    </td>
                  </tr>
                } @empty {
                  <tr>
                    <td appUiDataTableEmptyCell colspan="6">
                      <app-ui-empty-state
                        compact
                        title="Nenhuma chave criada"
                        description="Chaves de API emitidas para contas de serviço aparecerão aqui."
                      />
                    </td>
                  </tr>
                }
              </tbody>
            </table>
          </div>
        </div>
      </section>

      @if (creatingServiceAccount()) {
        <div appDialogOverlay>
          <section appDialog class="!w-[min(32rem,calc(100vw-2rem))] !max-w-none">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 appDialogTitle class="text-base">Conta de serviço</h3>
                <p appDialogDescription>
                  Use uma conta por integração ou automação para isolar permissões e chaves.
                </p>
              </div>
              <button
                appButton
                type="button"
                variant="ghost"
                size="sm"
                (click)="closeCreateServiceAccount()"
                aria-label="Fechar"
              >
                <ng-icon name="heroXMark" size="16" aria-hidden="true" />
              </button>
            </div>

            <form
              class="mt-5 grid gap-4"
              [formGroup]="serviceAccountForm"
              (ngSubmit)="createServiceAccount()"
            >
              <label class="grid gap-1.5">
                <span appUiLabel>Nome</span>
                <input
                  appInput
                  formControlName="name"
                  autocomplete="off"
                  placeholder="Integração de produção"
                  [readonly]="!canCreateServiceAccounts()"
                />
              </label>
              <label class="grid gap-1.5">
                <span appUiLabel>Onde será usada</span>
                <textarea
                  appTextarea
                  formControlName="description"
                  placeholder="Sistema, automação ou fluxo responsável por esta identidade."
                  [readonly]="!canCreateServiceAccounts()"
                ></textarea>
              </label>
              <div class="grid gap-1.5">
                <span appUiLabel id="service-account-kind-label"
                  >Uso principal</span
                >
                <div
                  appSelect
                  aria-labelledby="service-account-kind-label"
                  [ngpSelectValue]="serviceAccountForm.controls.kind.value"
                  [ngpSelectDisabled]="!canCreateServiceAccounts()"
                  (ngpSelectValueChange)="selectServiceAccountKind($event)"
                >
                  <span>{{ serviceAccountKindLabel(serviceAccountForm.controls.kind.value) }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (kind of serviceAccountKindOptions; track kind.value) {
                      <div appSelectOption [ngpSelectOptionValue]="kind.value">
                        {{ kind.label }}
                      </div>
                    }
                  </div>
                </div>
              </div>
              <div class="grid gap-1.5">
                <span appUiLabel id="service-account-roles-label"
                  >Funções</span
                >
                <div
                  appSelect
                  aria-labelledby="service-account-roles-label"
                  [ngpSelectMultiple]="true"
                  [ngpSelectValue]="serviceAccountRoleIds()"
                  [ngpSelectDisabled]="!canAssignRoles()"
                  (ngpSelectValueChange)="selectServiceAccountRoles($event)"
                  [attr.title]="!canAssignRoles() ? assignRolePermissionHint : null"
                >
                  <span class="min-w-0 truncate">{{
                    roleSelectionLabel(serviceAccountRoleIds())
                  }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (role of assignableRoles(); track role.id) {
                      <div appSelectOption [ngpSelectOptionValue]="role.id">
                        <span class="min-w-0 truncate">{{ role.name }}</span>
                        @if (serviceAccountRoleIds().includes(role.id)) {
                          <ng-icon name="heroCheck" size="15" aria-hidden="true" />
                        }
                      </div>
                    }
                  </div>
                </div>
                <p class="text-xs leading-5 text-muted">
                  {{ selectedServiceAccountPermissionCount() }} permissões concedidas.
                </p>
              </div>
              <div class="flex justify-end gap-2">
                <button
                  appButton
                  type="button"
                  variant="secondary"
                  (click)="closeCreateServiceAccount()"
                >
                  Cancelar
                </button>
                <button
                  appButton
                  type="submit"
                  variant="primary"
                  [disabled]="
                    saving() ||
                    serviceAccountForm.invalid ||
                    serviceAccountRoleIds().length === 0 ||
                    !canCreateServiceAccounts() ||
                    !canAssignRoles()
                  "
                >
                  <ng-icon name="heroPlus" size="16" aria-hidden="true" />
                  Salvar conta
                </button>
              </div>
            </form>
          </section>
        </div>
      }

      @if (creatingApiKey()) {
        <div appDialogOverlay>
          <section appDialog class="!w-[min(32rem,calc(100vw-2rem))] !max-w-none">
            <div class="flex flex-wrap items-start justify-between gap-3">
              <div>
                <h3 appDialogTitle class="text-base">Chave de API</h3>
                <p appDialogDescription>A chave será exibida uma única vez após a criação.</p>
              </div>
              <button
                appButton
                type="button"
                variant="ghost"
                size="sm"
                (click)="closeCreateApiKey()"
                aria-label="Fechar"
              >
                <ng-icon name="heroXMark" size="16" aria-hidden="true" />
              </button>
            </div>

            <form class="mt-5 grid gap-4" [formGroup]="apiKeyForm" (ngSubmit)="createApiKey()">
              <div class="grid gap-1.5">
                <span appUiLabel id="api-key-service-account-label"
                  >Conta de serviço</span
                >
                <div
                  appSelect
                  aria-labelledby="api-key-service-account-label"
                  [ngpSelectValue]="apiKeyForm.controls.serviceAccountId.value"
                  [ngpSelectDisabled]="!canCreateApiKeys()"
                  (ngpSelectValueChange)="selectApiKeyServiceAccount($event)"
                >
                  <span>{{ serviceAccountLabel(apiKeyForm.controls.serviceAccountId.value) }}</span>
                  <div *ngpSelectPortal appSelectDropdown>
                    @for (account of serviceAccountsAvailableForKeys(); track account.id) {
                      <div appSelectOption [ngpSelectOptionValue]="account.id">
                        {{ account.name }}
                      </div>
                    }
                  </div>
                </div>
              </div>
              <label class="grid gap-1.5">
                <span appUiLabel>Nome da chave</span>
                <input
                  appInput
                  formControlName="name"
                  autocomplete="off"
                  placeholder="Produção"
                  [readonly]="!canCreateApiKeys()"
                />
              </label>
              <label class="grid gap-1.5">
                <span appUiLabel>Expiração opcional</span>
                <input
                  appInput
                  type="datetime-local"
                  formControlName="expiresAt"
                  [readonly]="!canCreateApiKeys()"
                />
              </label>
              <div class="flex justify-end gap-2">
                <button appButton type="button" variant="secondary" (click)="closeCreateApiKey()">
                  Cancelar
                </button>
                <button
                  appButton
                  type="submit"
                  variant="primary"
                  [disabled]="saving() || apiKeyForm.invalid || !canCreateApiKeys()"
                >
                  <ng-icon name="heroKey" size="16" aria-hidden="true" />
                  Gerar chave
                </button>
              </div>
            </form>
          </section>
        </div>
      }
    }
  }
</div>
